import { Link, useParams } from 'react-router-dom';
import { useApi } from '../api';
import { Card, ErrorBanner, Loading, StatusBadge, formatDateTime } from '../components/ui';
import { useT } from '../i18n';

type RunData = {
  run: {
    id: string;
    creatorId: string;
    status: string;
    currentStep: string | null;
    completedSteps: string[];
    deferredUntil: string | null;
    estimatedNeurons: number;
    error: { code: string; message: string; retryable: boolean } | null;
  };
  steps: {
    step: string;
    status: string;
    attempt: number;
    startedAt: string;
    finishedAt: string | null;
    errorMessage: string | null;
  }[];
};

/** Los 17 pasos, en orden. Se muestran todos para que se vea lo que falta, no sólo lo hecho. */
const ALL_STEPS = [
  'VALIDATE_QUOTA',
  'CREATE_CREATOR_RECORD',
  'GENERATE_CHARACTER_BIBLE',
  'VALIDATE_IDENTITY',
  'GENERATE_USERNAME_SUGGESTIONS',
  'PROVISION_EMAIL_IDENTITY',
  'GENERATE_REFERENCE_CANDIDATES',
  'MODERATE_REFERENCE_CANDIDATES',
  'SELECT_CANONICAL_REFERENCE',
  'GENERATE_INITIAL_CONTENT_SET',
  'MODERATE_INITIAL_CONTENT',
  'CREATE_CONTENT_CALENDAR',
  'CREATE_PLATFORM_ONBOARDING_TASKS',
  'PUBLISH_TO_CONNECTED_PLATFORMS',
  'WAIT_FOR_HUMAN_GATES',
  'RESUME_PLATFORM_CONNECTIONS',
  'FINALIZE_CREATOR',
];

/**
 * Seguimiento de una corrida en vivo.
 *
 * Sondea cada tres segundos mientras el workflow avanza. Es la pantalla que ve el
 * administrador justo después de pulsar el botón, y lo que evita que la espera de varios
 * minutos parezca que la aplicación se colgó.
 */
export function WorkflowRun() {
  const { t } = useT();
  const { id } = useParams<{ id: string }>();
  const state = useApi<RunData>(id ? `/api/workflows/${id}` : null, 3000);

  if (state.loading && !state.data) return <Loading label={t('corrida.iniciando')} />;
  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!state.data) return null;

  const { run, steps } = state.data;
  const done = new Set(run.completedSteps);
  const byStep = new Map(steps.map((step) => [step.step, step]));

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl font-semibold tracking-tight">{t('corrida.titulo')}</h1>
        <StatusBadge status={run.status} />
      </div>

      {run.status === 'DEFERRED' && run.deferredUntil && (
        <div className="rounded-xl border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/10 p-4">
          <p className="text-sm font-medium text-[var(--color-warn)]">
            {t('corrida.cuotaAgotada')}
          </p>
          <p className="mt-1 text-sm">
            {t('corrida.reinicio', { cuando: formatDateTime(run.deferredUntil) })}
          </p>
        </div>
      )}

      {run.error && run.status === 'FAILED' && (
        <div
          role="alert"
          className="rounded-xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/10 p-4"
        >
          <p className="text-sm font-semibold text-[var(--color-danger)]">{run.error.code}</p>
          <p className="mt-1 text-sm">{run.error.message}</p>
          {!run.error.retryable && (
            <p className="mt-2 text-xs text-[var(--color-ink-muted)]">
              {t('corrida.noSeReintenta')}
            </p>
          )}
        </div>
      )}

      <Card title={t('corrida.pasos', { hechos: done.size, total: ALL_STEPS.length })}>
        <ol className="space-y-2">
          {ALL_STEPS.map((step) => {
            const record = byStep.get(step);
            const isDone = done.has(step);
            const isCurrent = run.currentStep === step && !isDone;

            return (
              <li key={step} className="flex items-start gap-3 text-sm">
                <span
                  aria-hidden="true"
                  className={
                    isDone
                      ? 'text-[var(--color-ok)]'
                      : isCurrent
                        ? 'text-[var(--color-accent)]'
                        : 'text-[var(--color-ink-muted)]'
                  }
                >
                  {isDone ? '✓' : isCurrent ? '▸' : '·'}
                </span>
                <div className="min-w-0 flex-1">
                  <span
                    className={
                      isCurrent ? 'font-medium' : isDone ? '' : 'text-[var(--color-ink-muted)]'
                    }
                  >
                    {step}
                  </span>
                  {record?.errorMessage && (
                    <p className="mt-0.5 text-xs text-[var(--color-danger)]">
                      {record.errorMessage}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        <p className="mt-4 text-xs text-[var(--color-ink-muted)]">
          {t('corrida.consumoEstimado', { n: run.estimatedNeurons.toFixed(0) })}
        </p>
      </Card>

      {run.creatorId && (
        <Link
          to={`/influencer/${run.creatorId}`}
          className="inline-flex items-center rounded-lg bg-[var(--color-accent)] px-5 py-2.5 text-sm font-semibold text-white"
        >
          {t('corrida.verInfluencer')}
        </Link>
      )}
    </div>
  );
}
